<!-- Full-page (or contained) loading overlay with the STEKZ three-bar animation. -->
<template>
    <div
        v-bind:class="{ contained: contained }"
        class="spinner"
        ref="spinnerElement"
    >
        <div class="loading">
            <div class="bars">
                <span class="bar"></span>
                <span class="bar"></span>
                <span class="bar"></span>
            </div>
            <p v-if="text">{{ text }}</p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ZIndex } from "@primeuix/utils/zindex";

import { usePrimeVue } from "primevue/config";
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const props = withDefaults(
    defineProps<{
        contained?: boolean;
        autoZIndex?: boolean;
        text?: string;
    }>(),
    {
        autoZIndex: false,
    }
);

const primevue = usePrimeVue();
const spinnerElement = ref<HTMLElement>();

onMounted(async () => {
    if (props.autoZIndex) {
        await nextTick();
        ZIndex.set(
            "modal",
            spinnerElement.value!,
            primevue.config.zIndex?.modal
        );
    }
});

onBeforeUnmount(() => {
    if (props.autoZIndex) {
        ZIndex.clear(spinnerElement.value!);
    }
});
</script>

<style scoped lang="scss">
.spinner {
    z-index: 9;
    position: fixed;
    display: block;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.5);

    .loading {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    p {
        margin-top: 1rem;
        color: var(--text-color-secondary);
        font-size: 0.875rem;
    }

    &.contained {
        position: relative;

        &::after {
            display: table;
            content: "";
            clear: both;
        }

        .loading {
            position: absolute;
        }
    }
}

/* Three horizontal bars referencing the STEKZ logo mark */
.bars {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .bar {
        display: block;
        width: 40px;
        height: 6px;
        background-color: var(--stekz-primary-color);
        transform-origin: left center;

        &:nth-child(1) {
            background-color: var(--stekz-primary-color);
            animation: stekz-bar 1.2s ease-in-out 0s infinite;
        }
        &:nth-child(2) {
            background-color: var(--stekz-secondary-color);
            animation: stekz-bar 1.2s ease-in-out -0.2s infinite;
        }
        &:nth-child(3) {
            background-color: var(--stekz-tertiary-color);
            animation: stekz-bar 1.2s ease-in-out -0.4s infinite;
        }
    }
}

@keyframes stekz-bar {
    0%,
    100% {
        transform: scaleX(1);
        opacity: 1;
    }
    50% {
        transform: scaleX(0.25);
        opacity: 0.35;
    }
}
</style>
